<template>
  <CustomTable ref="table" class="compact-table" :elements=elements :data="data" :searchOptions="searchOptions" :loadData="refreshView">
    <template v-slot:header-options>
      <div class="sub-filters" role="group" aria-label="Filtrer les publications">
        <button v-for="filter in visibleQuickFilters" :key="filter.label" type="button" class="sub-filter-button" :class="{active: quickFilterLabel === filter.label}" :aria-pressed="quickFilterLabel === filter.label" @click="quickFilterLabel = filter.label">
          {{ filter.label }} <span class="count">{{ countOf(filter) }}</span>
        </button>
      </div>
    </template>
    <template v-slot:table>
      <thead>
        <tr>
          <custom-header field="topic.title" class="col-fill">Sujet</custom-header>
          <custom-header field="topic.funnelStage" class="hide-for-mobile col-fit">Intention</custom-header>
          <custom-header field="status" class="col-fit">Statut</custom-header>
          <custom-header field="text.status" class="hide-for-mobile col-fit">Préparation</custom-header>
          <custom-header field="date" class="col-fit">Date</custom-header>
          <custom-header field="metrics.impressions" class="hide-for-mobile col-fit">Impressions</custom-header>
          <custom-header field="engagement" class="hide-for-mobile col-fit">Engagement</custom-header>
          <custom-header field="actions" class="col-fit" :sortable="false">Actions</custom-header>
        </tr>
      </thead>
      <tbody v-if="rows().length > 0">
        <tr v-for="publication in rows()" :key="publication.id" class="business-row" @click="open(publication)">
          <custom-body field="topic.title" class="col-fill cell-strong" :title="publication.topic.title">{{ publication.topic.title }}<small class="channel">{{ channelLabel(publication.channel) }}</small></custom-body>
          <custom-body field="topic.funnelStage" class="hide-for-mobile col-fit"><FunnelStageBadge :stage="publication.topic.funnelStage" /></custom-body>
          <custom-body field="status" class="col-fit"><StatusBadge :status="publication.status" :status-enum="PUBLICATION_STATUS_ENUM" /></custom-body>
          <custom-body field="text.status" class="hide-for-mobile col-fit">
            <span class="badges">
              <StatusBadge :status="publication.text.status" :status-enum="TEXT_STATUS_ENUM" :label="textLabel(publication)" />
              <StatusBadge v-if="publication.image.required" :status="publication.image.status" :status-enum="IMAGE_STATUS_ENUM" :label="imageLabel(publication)" />
            </span>
          </custom-body>
          <custom-body field="date" class="col-fit">{{ dateText(publication) }}</custom-body>
          <custom-body field="metrics.impressions" class="hide-for-mobile col-fit">
            <template v-if="publication.status === PUBLISHED">
              <span v-if="publication.metrics" class="numeric">{{ formatNumber(publication.metrics.impressions) }}</span>
              <span v-else class="to-fill" title="Aucun résultat saisi pour cette publication">À saisir</span>
            </template>
            <span v-else class="none">-</span>
          </custom-body>
          <custom-body field="engagement" class="hide-for-mobile col-fit"><span class="numeric">{{ engagementText(publication) }}</span></custom-body>
          <custom-body field="actions" class="col-fit actions">
            <button class="button-icon-custom" title="Ouvrir le sujet" @click.stop="$refs.topicModal.openModal(publication.topic)"><span class="fas fa-lightbulb"></span></button>
            <button class="button-icon-custom" title="Supprimer la publication" @click.stop="remove(publication)"><span class="fas fa-trash-can"></span></button>
          </custom-body>
        </tr>
      </tbody>
      <tbody v-else><tr class="empty-row"><td colspan="8" style="text-align: center">{{ emptyText }}</td></tr></tbody>
    </template>
  </CustomTable>
  <TopicModal ref="topicModal" :callback=changed :openPublication=openPublication />
  <PublicationModal ref="publicationModal" :callback=changed :closedCallback=publicationClosed />
  <ConfirmModal ref="deleteConfirmModal" title="Suppression" text="Supprimer cette publication ?" yes="Supprimer" no="Retour"/>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faLightbulb, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import CustomTable from "@/components/CustomTable.vue";
import CustomHeader from "@/components/tables/CustomHeader.vue";
import CustomBody from "@/components/tables/CustomBody.vue";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import ConfirmModal from "@/components/modals/confirmModal.vue";
import {NotificationService} from "@vues/v-common";
import {SORT_OPERATOR_ENUM} from "@/models/sortOperatorEnum";
import {CustomTableService} from "@/services/CustomTableService";
import {PublicationController} from "@/marketing/controllers/PublicationController";
import TopicModal from "@/marketing/components/modals/TopicModal.vue";
import PublicationModal from "@/marketing/components/modals/PublicationModal.vue";
import FunnelStageBadge from "@/marketing/components/FunnelStageBadge.vue";
import {queryRows} from "@/marketing/tableQuery";
import {
  CHANNEL_ENUM, FUNNEL_STAGE_FILTER_ENUM, IMAGE_STATUS_ENUM, PUBLICATION_STATUS_ENUM, TEXT_STATUS_ENUM,
  formatDate, formatNumber, idOf, labelOf
} from "@/marketing/utils";
library.add(faLightbulb, faTrashCan);

const PUBLISHED = PUBLICATION_STATUS_ENUM.PUBLISHED.value;

// Libellés des pastilles « Préparation » : le texte et l'image se lisent dans la même colonne
const TEXT_LABELS = {NONE: "Texte à écrire", DRAFT: "Texte : brouillon", FINAL: "Texte : terminé"};
const IMAGE_LABELS = {TO_DO: "Image à faire", GENERATED: "Image : générée", VALIDATED: "Image : validée"};

// Filtres rapides sur le statut de diffusion ; le premier est choisi par défaut
const QUICK_FILTERS = [
  {label: "À publier", statuses: [PUBLICATION_STATUS_ENUM.DRAFT.value, PUBLICATION_STATUS_ENUM.SCHEDULED.value]},
  {label: "Programmées", statuses: [PUBLICATION_STATUS_ENUM.SCHEDULED.value]},
  {label: "Publiées", statuses: [PUBLISHED]},
  {label: "À classer", unclassified: true},
  {label: "Toutes", statuses: undefined},
];

/**
 * Tableau des publications (écran Publications) : ce qui reste à publier, ce qui est programmé, ce qui est publié et ses résultats.
 * Le service renvoie toutes les publications du groupe avec leur sujet : recherche, filtres et tris s'appliquent ici.
 * Un clic ouvre la publication (directement sur les résultats quand une publication publiée n'en a pas encore).
 */
export default {
  name: 'PublicationTable',
  components: {PublicationModal, TopicModal, StatusBadge, ConfirmModal, CustomBody, CustomHeader, CustomTable, FunnelStageBadge},
  props: ['changedCallback'],
  data() {
    return {
      PUBLICATION_STATUS_ENUM, TEXT_STATUS_ENUM, IMAGE_STATUS_ENUM, PUBLISHED,
      data: [],
      quickFilters: QUICK_FILTERS,
      quickFilterLabel: QUICK_FILTERS[0].label,
      elements: [
        {label: "Sujet", value: "topic.title", type: "text"},
        {label: "Intention", value: "topic.funnelStage", type: "select", values: FUNNEL_STAGE_FILTER_ENUM},
        {label: "Réseau", value: "channel", type: "select", values: CHANNEL_ENUM},
        {label: "Statut", value: "status", type: "select", values: PUBLICATION_STATUS_ENUM},
        {label: "Texte", value: "text.status", type: "select", values: TEXT_STATUS_ENUM},
        {label: "Date", value: "date", type: "date"},
        {label: "Impressions", value: "metrics.impressions", type: "number"},
        {label: "Engagement (%)", value: "engagement", type: "number"},
        {label: "Actions", value: undefined, type: undefined},
      ],
      searchOptions: {
        filters: [],
        sorts: [{field: "date", order: SORT_OPERATOR_ENUM.DESCENDING.value}]
      }
    };
  },
  computed: {
    // « À classer » n'est proposé que s'il y a des publications importées à classer (ou s'il est choisi)
    visibleQuickFilters() {
      return this.quickFilters.filter(filter => !filter.unclassified || filter.label === this.quickFilterLabel || this.countOf(filter) > 0);
    },
    emptyText() {
      return this.data.length === 0 ? "Aucune publication : elles se créent avec les sujets de l'écran Contenus" : "Aucune publication ne correspond";
    },
  },
  async mounted() {
    // Toutes les lignes sont affichées : pas de pagination
    const pagination = this.$refs.table.$refs.paginationArray;
    pagination.paginationInputs.pages.readOnly = true;
    pagination.isNextReloadCancel = true;
    pagination.paginationInputs.pages.value = undefined;
    pagination.index = undefined;

    await this.loadData();
  },
  methods: {
    formatNumber,
    channelLabel(channel) {
      return labelOf(CHANNEL_ENUM, channel, channel);
    },
    textLabel(publication) {
      return TEXT_LABELS[publication.text.status] ?? labelOf(TEXT_STATUS_ENUM, publication.text.status, '');
    },
    imageLabel(publication) {
      return IMAGE_LABELS[publication.image.status] ?? labelOf(IMAGE_STATUS_ENUM, publication.image.status, '');
    },
    // Date de la publication, ou de sa programmation tant qu'elle n'est pas publiée
    dateText(publication) {
      if (publication.status === PUBLISHED) return publication.publishedAt ? formatDate(publication.publishedAt) : "Date non définie";
      return publication.scheduledAt ? formatDate(publication.scheduledAt) : '-';
    },
    engagementText(publication) {
      return typeof publication.engagement === 'number' ? `${publication.engagement.toLocaleString('fr-FR', {maximumFractionDigits: 1})} %` : '-';
    },
    matchesQuickFilter(publication, filter) {
      if (filter.unclassified) return !publication.topic?.funnelStage;
      return !filter.statuses || filter.statuses.includes(publication.status);
    },
    countOf(filter) {
      return this.data.filter(publication => this.matchesQuickFilter(publication, filter)).length;
    },
    // Lignes affichées : filtre rapide, recherche libre, puis filtres et tris avancés du tableau
    rows() {
      const filter = this.quickFilters.find(item => item.label === this.quickFilterLabel);
      let rows = this.data.filter(publication => this.matchesQuickFilter(publication, filter));
      if (!this.$refs.table) return rows;
      rows = this.$refs.table.$refs.searchArray.simpleFilter(rows);
      const state = CustomTableService.data.get(this.$refs.table.tableId);
      return state ? queryRows(rows, state.currentFilters, state.currentSort) : rows;
    },
    // Filtres et tris sont appliqués à l'affichage : rien à recharger
    async refreshView() {},
    normalize(publication) {
      const metrics = publication.metrics;
      const known = ['impressions', 'reactions', 'comments', 'shares'].every(key => typeof metrics?.[key] === 'number');
      return {
        ...publication,
        id: idOf(publication),
        text: publication.text ?? {status: 'NONE'},
        image: publication.image ?? {required: false, status: 'NOT_REQUIRED'},
        topic: {...publication.topic, id: idOf(publication.topic)},
        // Date utilisée pour le tri : publication, sinon programmation
        date: publication.status === PUBLISHED ? publication.publishedAt : publication.scheduledAt,
        // Part des impressions qui ont donné une réaction, un commentaire ou un partage ; inconnue sans ces quatre chiffres
        engagement: known && metrics.impressions > 0 ? (metrics.reactions + metrics.comments + metrics.shares) * 100 / metrics.impressions : undefined,
      };
    },
    async loadData() {
      this.$refs.table?.startLoading();
      const publications = await PublicationController.getTable();
      if (publications && !publications.errors) this.data = publications.map(publication => this.normalize(publication));
      this.$refs.table?.stopLoading();
    },
    async changed() {
      await this.loadData();
      if (this.changedCallback) this.changedCallback();
    },
    open(publication) {
      const needsResults = publication.status === PUBLISHED && !publication.metrics;
      this.$refs.publicationModal.openModal(publication.id, needsResults ? 'results' : 'writing');
    },
    openPublication(publicationId) {
      this.$refs.publicationModal.openModal(publicationId);
    },
    // La modale d'une publication ouverte depuis un sujet se referme : le sujet redevient la modale active
    publicationClosed() {
      this.$refs.topicModal?.reloadPublications();
      this.$refs.topicModal?.refocus();
    },
    async remove(publication) {
      const res = await this.$refs.deleteConfirmModal.displayChoice(() => PublicationController.delete(publication.id));
      if (res !== false && !(res && res.errors)) {
        (new NotificationService).addNotification("success", "Publication supprimée");
        await this.changed();
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/common/style/businessTable.styl';

// Le nombre de publications du filtre, en retrait du libellé
.count
  opacity 0.7
  font-variant-numeric tabular-nums

.numeric
  font-variant-numeric tabular-nums

// Les deux icônes d'action restent côte à côte (la ligne garde une hauteur compacte)
.actions
  white-space nowrap

.none
  color CTRL_TEXT_MUTED

// Réseau sous le titre du sujet
.channel
  display block
  color CTRL_TEXT_MUTED
  font-size 0.75em
  font-weight 500
  line-height 1.1

// Texte et image : deux pastilles sur une ligne
.badges
  display inline-flex
  align-items center
  gap 6px

// Une publication publiée sans résultats saisis : à compléter
.to-fill
  padding 1px 8px
  border-radius 20px
  background #fff3cd
  font-size 0.85em
  font-weight 600
  white-space nowrap
</style>
